'use client';

import { useRef, useState } from 'react';

import type { SelectProps as ArkSelectProps } from '@ark-ui/react';
import { Select as ArkSelect, Portal } from '@ark-ui/react';
import { Checkmark20Regular, ChevronDown20Filled } from '@fluentui/react-icons';
import { X } from 'lucide-react';

import { cn, floatingLabelStyles } from '@avodah-inc/ui/utils';

import { ScrollArea } from '../scroll-area';

export type SelectOption = {
  label: string;
  value: string;
};

export type BaseSelectProps<T extends SelectOption> = ArkSelectProps<T> & {
  clearable?: boolean;
  showValue?: boolean;
  placeholder?: string;
  label?: string;
  isInvalid?: boolean;
  disableValueTruncation?: boolean;
  showCheckIcon?: boolean;
  mainAxisOffset?: number;
  classNames?: {
    text?: string;
    trigger?: string;
    content?: string;
  };
  dataTestId?: string;
};

export const BaseSelect = <T extends SelectOption>({
  items,
  placeholder,
  showValue = true,
  clearable = true,
  disableValueTruncation = false,
  label,
  isInvalid,
  classNames,
  showCheckIcon = true,
  mainAxisOffset = 12,
  ...props
}: BaseSelectProps<T>) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [popoverPlacement, setPopverPlacement] = useState('bottom');
  const [selectedValue, setSelectedValue] = useState(
    props.value?.[0] || props.defaultValue,
  );

  return (
    <ArkSelect.Root
      onClick={() => {
        const placement = triggerRef.current?.getAttribute('data-placement');
        if (placement) setPopverPlacement(placement);
      }}
      items={items}
      positioning={{
        sameWidth: true,
        offset: {
          mainAxis: mainAxisOffset,
        },
      }}
      className={cn('relative', props.className)}
      {...props}
    >
      <ArkSelect.Control className="relative">
        <ArkSelect.Trigger
          ref={triggerRef}
          data-invalid={isInvalid}
          data-testid={props.dataTestId}
          className={cn(
            'text-body-3 data-[invalid=true]:border-red focus:border-primary group flex h-auto w-full items-center justify-between gap-2 rounded border border-gray-300 bg-white px-4 py-2 text-gray-900 disabled:cursor-not-allowed',
            'data-[state=open]:data-[placement=bottom]:rounded-b-none data-[state=open]:data-[placement=bottom]:border-b-0',
            'data-[state=open]:data-[placement=top]:rounded-t-none data-[state=open]:data-[placement=top]:border-t-0',
            classNames?.trigger,
          )}
        >
          {label && (
            <ArkSelect.Label
              className={cn(
                floatingLabelStyles as string,
                'group-data-[state=open]:group-data-[placement=top]:hidden',
                classNames?.trigger,
              )}
            >
              {label}
            </ArkSelect.Label>
          )}
          {showValue ? (
            <ArkSelect.ValueText
              placeholder={placeholder}
              className={cn(
                'truncate group-disabled:opacity-50',
                disableValueTruncation ? 'flex-wrap whitespace-normal' : '',
                selectedValue ? 'text-gray-900' : 'text-gray-500',
                classNames?.text,
              )}
            />
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
          <ArkSelect.Indicator className="group flex items-center">
            <ChevronDown20Filled className="text-gray-400 transition-all group-disabled:opacity-50 group-data-[state=open]:rotate-180" />
          </ArkSelect.Indicator>
        </ArkSelect.Trigger>
        {selectedValue && (
          <ArkSelect.ClearTrigger
            onClick={() => {
              setSelectedValue('');
            }}
            className={cn(
              'transform-all absolute right-5 top-3 bg-white active:translate-y-0.5',
              {
                hidden: !clearable,
              },
            )}
          >
            <X size={16} className="text-gray-400" />
          </ArkSelect.ClearTrigger>
        )}

        <Portal>
          <ArkSelect.Positioner>
            <ArkSelect.Content
              className={cn(
                'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out z-[100] h-fit overflow-hidden rounded border border-gray-300 bg-white outline-none',
                {
                  'shadow-surface-2 data-[state=open]:rounded-t-none':
                    popoverPlacement === 'bottom',
                  'data-[state=open]:rounded-b-none':
                    popoverPlacement === 'top',
                },
                classNames?.content,
              )}
            >
              <ScrollArea
                className={cn({
                  'h-[12.5rem]': items.length > 5,
                })}
              >
                <ArkSelect.ItemGroup id="framework">
                  {items.map((item) => (
                    <ArkSelect.Item
                      onClick={() => {
                        setSelectedValue(item.value);
                      }}
                      key={item.value}
                      item={item}
                      className="hover:bg-primary-50 focus:outline-primary-300 group flex cursor-pointer justify-between gap-2 px-4 py-2 outline-none focus:outline-1 focus:outline-offset-1"
                    >
                      <ArkSelect.ItemText className="group-selected:font-medium flex items-center gap-2 truncate font-normal text-gray-900">
                        <p className="truncate capitalize">{item.label}</p>
                      </ArkSelect.ItemText>
                      {selectedValue && showCheckIcon && (
                        <ArkSelect.ItemIndicator className="text-primary-500 flex w-5 items-center data-[state=unchecked]:text-transparent">
                          <Checkmark20Regular />
                        </ArkSelect.ItemIndicator>
                      )}
                    </ArkSelect.Item>
                  ))}
                </ArkSelect.ItemGroup>
              </ScrollArea>
            </ArkSelect.Content>
          </ArkSelect.Positioner>
        </Portal>
      </ArkSelect.Control>
    </ArkSelect.Root>
  );
};

type SingleSelectionType<T extends SelectOption> = {
  onValueChange?: (e: { item: T; value: string }) => void;
};

export type SelectProps<T extends SelectOption> = Omit<
  BaseSelectProps<T>,
  'onValueChange' | 'multiple'
>;

export const Select = <T extends SelectOption>({
  onValueChange,
  ...props
}: SelectProps<T> & SingleSelectionType<T>) => {
  return (
    <BaseSelect
      {...props}
      onValueChange={(e) => {
        if (!e.items[0]) return;
        onValueChange?.({
          item: e.items[0],
          value: e.value[0] ?? '',
        });
      }}
    />
  );
};
